import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import { geolocationHeader } from '../lib/geolocation';
import { toText } from '../lib/toText';
import type {
  WithdrawalApproveResult,
  WithdrawalChecklist,
  WithdrawalListResponse,
  WithdrawalPenaltyPreview,
  WithdrawalRequest,
  WithdrawalRequestPayload,
  WithdrawalRetentionContext,
  WithdrawalStatus,
} from '../types/finance';

interface ListFilters {
  status?: WithdrawalStatus[];
  user_id?: string;
  page?: number;
  size?: number;
}

interface ApproveChecklistAcks {
  account_ownership_reviewed: boolean;
  account_change_reviewed: boolean;
}

class WithdrawalsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** Client originates a withdrawal, signed with a fresh MFA step-up code.
   *  Precise browser location is captured and sent as a hard gate (server
   *  returns 428 without it). Throws GeolocationRequiredError if denied. */
  async request(payload: WithdrawalRequestPayload): Promise<{ request_id: string }> {
    const headers = { ...(await geolocationHeader()), 'Idempotency-Key': crypto.randomUUID() };
    const res = await this.api.post<{ request_id: string }>(
      '/api/v1/withdrawals/request', payload, { headers },
    );
    return res.data;
  }

  /** What withdrawing this amount would cost the signed-in client right now, so the
   *  exact charge can be shown before they confirm anything. Scoped to the caller's
   *  own position server-side. */
  async penaltyPreview(productId: string, amount: string): Promise<WithdrawalPenaltyPreview> {
    const res = await this.api.get<WithdrawalPenaltyPreview>(
      '/api/v1/withdrawals/penalty-preview',
      { params: { product_id: productId, amount } },
    );
    return res.data;
  }

  /** Agent escalates a requested withdrawal to the manager (retention failed). */
  async escalate(
    requestId: string, expectedVersion: number, note?: string,
  ): Promise<{ request_id: string }> {
    const res = await this.api.post<{ request_id: string }>(
      `/api/v1/withdrawals/${requestId}/escalate`,
      { expected_version: expectedVersion, note: note ?? null },
    );
    return res.data;
  }

  /** Cancel a requested withdrawal (client withdrew it, or retention succeeded). */
  async cancel(
    requestId: string, expectedVersion: number, reason?: string,
  ): Promise<{ request_id: string }> {
    const res = await this.api.post<{ request_id: string }>(
      `/api/v1/withdrawals/${requestId}/cancel`,
      { expected_version: expectedVersion, reason: reason ?? null },
    );
    return res.data;
  }

  /** Manager-checklist signals (cooling-off + name match) before approval. */
  async getChecklist(requestId: string): Promise<WithdrawalChecklist> {
    const res = await this.api.get<WithdrawalChecklist>(
      `/api/v1/withdrawals/${requestId}/checklist`,
    );
    return res.data;
  }

  /** Agent call-guide retention context for a client: cooling-off window
   *  (days, from settings), whether they've withdrawn before, and the
   *  cooling-off state of any open withdrawal. */
  async retentionContext(userId: string): Promise<WithdrawalRetentionContext> {
    const res = await this.api.get<WithdrawalRetentionContext>(
      '/api/v1/withdrawals/retention-context',
      { params: { user_id: userId } },
    );
    return res.data;
  }

  async list(filters: ListFilters = {}): Promise<WithdrawalListResponse> {
    const res = await this.api.get<WithdrawalListResponse>('/api/v1/withdrawals', {
      params: filters,
      paramsSerializer: (params) => {
        const parts: string[] = [];
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null) continue;
          if (Array.isArray(value)) {
            for (const v of value) {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
            }
          } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(toText(value))}`);
          }
        }
        return parts.join('&');
      },
    });
    return res.data;
  }

  /** Manager approval — carries the checklist acks and a fresh TOTP
   *  step-up `mfa_code` (money-moving action). */
  async approve(
    requestId: string, expectedVersion: number, acks: ApproveChecklistAcks, mfaCode: string,
  ): Promise<WithdrawalApproveResult> {
    const res = await this.api.post<WithdrawalApproveResult>(
      `/api/v1/withdrawals/${requestId}/approve`,
      { expected_version: expectedVersion, ...acks, mfa_code: mfaCode },
    );
    return res.data;
  }

  async reject(
    requestId: string,
    expectedVersion: number,
    reason: string,
  ): Promise<{ request_id: string }> {
    const res = await this.api.post<{ request_id: string }>(
      `/api/v1/withdrawals/${requestId}/reject`,
      { expected_version: expectedVersion, reason },
    );
    return res.data;
  }

  async pendingCount(): Promise<number> {
    const res = await this.list({ status: ['pending'], size: 1 });
    return res.total;
  }

  async getOne(requestId: string): Promise<WithdrawalRequest | null> {
    const res = await this.list({ size: 100 });
    return res.items.find((w) => w.id === requestId) ?? null;
  }
}

export default new WithdrawalsService();
